import sha256 from "sha256";

import { isConfirmationPagePath } from "@/services/confirmationTemplateUpgrade";

function centsToAmount(cents) {
  return parseFloat(((cents || 0) / 100).toFixed(2));
}

function firstCartItem(cart) {
  if (!cart?.items) {
    return null;
  }

  return Object.values(cart.items)[0] ?? null;
}

export function shouldPushConfirmationDataLayer(
  pathname = window.location.pathname,
  globalConfig = window.globalConfig,
) {
  return (
    Boolean(globalConfig?.gtmIdentifier) && isConfirmationPagePath(pathname)
  );
}

export function shouldPushEntryDataLayer(globalConfig = window.globalConfig) {
  return Boolean(globalConfig?.gtmIdentifier);
}

export function buildEntryDataLayerPushes({
  globalConfig = window.globalConfig,
  productDetails = window.productDetails,
} = {}) {
  const pushes = [];
  const productCode = productDetails?.code;

  if (productCode) {
    pushes.push({ productCode });
  }

  if (globalConfig?.brandTrigram) {
    pushes.push({ buTrigram: globalConfig.brandTrigram });
  }

  if (globalConfig?.lang) {
    pushes.push({ lang: String(globalConfig.lang).toUpperCase() });
  }

  if (globalConfig?.isOrderPage && globalConfig.orderPageSlug) {
    pushes.push({ code: globalConfig.orderPageSlug });
  }

  return pushes;
}

export function pushEntryDataLayer({
  globalConfig = window.globalConfig,
  productDetails = window.productDetails,
} = {}) {
  try {
    if (!shouldPushEntryDataLayer(globalConfig)) {
      return false;
    }

    const pushes = buildEntryDataLayerPushes({
      globalConfig,
      productDetails,
    });

    if (pushes.length === 0) {
      return false;
    }

    window.dataLayer = window.dataLayer || [];
    for (const payload of pushes) {
      window.dataLayer.push(payload);
    }

    return true;
  } catch (error) {
    console.error("Failed to push entry dataLayer:", error);
    return false;
  }
}

export function buildConfirmationDataLayerItems(
  cart,
  globalConfig = window.globalConfig,
) {
  const gtmItems = [];

  if (!cart?.items) {
    return gtmItems;
  }

  for (const index in cart.items) {
    const item = cart.items[index];
    gtmItems.push({
      item_id: item.code,
      item_name: item.name,
      index: parseInt(index, 10),
      item_brand: globalConfig?.brandName,
      item_category2: item.choice.bcl ? "BCL" : "NBCL",
      price: centsToAmount(item.choice.unitPrice),
      quantity: item.qty,
      item_variant: "X" + item.choice.numberOfUnit,
    });
  }

  return gtmItems;
}

export function buildConfirmationDataLayerPayload({
  order,
  cart,
  urlParams = {},
  globalConfig = window.globalConfig,
}) {
  const pageLanguage = globalConfig?.lang
    ? globalConfig.lang.toUpperCase()
    : "FR";
  const firstItem = firstCartItem(cart);
  const shipping = centsToAmount(cart.shippingPrice);
  const value = centsToAmount(cart.subtotal);
  const price = centsToAmount(firstItem?.choice?.unitPrice);
  const paymentMethod = order.paymentType ?? null;

  return {
    pageLanguage,
    lang: pageLanguage,
    brandName: globalConfig?.brandName ?? null,
    buTrigram: globalConfig?.brandTrigram ?? null,
    currency: globalConfig?.currencyIsoCode ?? null,
    payment_type: paymentMethod,
    paymentMethod,
    hemail: sha256((order.billingEmail || "").toLowerCase()),
    trackingCode: urlParams.trackingCode ? urlParams.trackingCode : "XMISSING",
    externalPartnerId: urlParams.externalPartnerId
      ? urlParams.externalPartnerId
      : null,
    event: "order",
    transaction_id: order.externalOrderId,
    orderId: order.externalOrderId,
    productId: firstItem?.choice?.productId ?? firstItem?.id ?? null,
    productCode: firstItem?.code ?? null,
    offerName: firstItem?.choice?.name ?? firstItem?.name ?? null,
    type: firstItem?.choice?.recordType ?? null,
    price,
    quantity: firstItem?.qty ?? 1,
    tax: 0,
    shipping,
    value,
    total: parseFloat((shipping + value).toFixed(2)),
    isBcl: firstItem?.choice?.bcl === true,
    nextOrderId: null,
    items: buildConfirmationDataLayerItems(cart, globalConfig),
  };
}

export function pushConfirmationDataLayer(orderHistoryStore, trackingStore) {
  try {
    const lastHistoryItem = orderHistoryStore.activeLastOrderHistoryItem;

    if (!lastHistoryItem?.order || !lastHistoryItem?.cart) {
      return false;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      buildConfirmationDataLayerPayload({
        order: lastHistoryItem.order,
        cart: lastHistoryItem.cart,
        urlParams: trackingStore.urlParams,
      }),
    );

    return true;
  } catch (error) {
    console.error("Failed to push confirmation dataLayer:", error);
    return false;
  }
}
